import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { fmtCurrency } from '@/lib/formatters';
import { useToast } from './use-toast';

// ========== STATUS CONSTANTS ==========

/** Valores persistidos no banco de dados — NUNCA usar strings literais fora daqui */
export const BOLETIM_STATUS_DB = {
  RASCUNHO: 'rascunho',
  PENDENTE_APROVACAO_CLIENTE: 'pendente_aprovacao_cliente',
  REPROVADO: 'reprovado',
  APROVADO_PARA_FATURAMENTO: 'aprovado_para_faturamento',
} as const;

export type BoletimStatusDB = typeof BOLETIM_STATUS_DB[keyof typeof BOLETIM_STATUS_DB];

export interface EmpBoletimMedicao {
  id: string;
  contrato_id: string;
  planilha_versao_id: string;
  numero_boletim: number;
  periodo_inicio: string;
  periodo_fim: string;
  status: BoletimStatusDB;
  valor_bruto: number | null;
  valor_glosas: number | null;
  valor_liquido: number | null;
  percentual_fisico_periodo: number | null;
  percentual_fisico_acumulado: number | null;
  percentual_financeiro_periodo: number | null;
  percentual_financeiro_acumulado: number | null;
  observacoes: string | null;
  aprovado_por: string | null;
  aprovado_em: string | null;
  motivo_reabertura: string | null;
  reaberto_por: string | null;
  reaberto_em: string | null;
  parecer_cliente: 'aprovado_integral' | 'reprovado' | null;
  parecer_observacoes: string | null;
  parecer_em: string | null;
  parecer_por: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export type MedicaoTipoOrigem = 'ppu' | 'eap' | 'ubs' | 'ubs_ppu';

export interface EmpMedicaoItem {
  id: string;
  boletim_id: string;
  planilha_item_id: string;
  quantidade_medida: number;
  quantidade_acumulada_anterior: number;
  quantidade_acumulada_atual: number;
  valor_unitario: number;
  valor_periodo: number;
  valor_acumulado: number;
  percentual_executado: number | null;
  observacoes: string | null;
  origem_quantidade: 'automatico' | 'ajuste_fiscal';
  // F4/F5/F6: rota de cálculo da linha (ppu direto / eap / ubs puro / ubs→ppu híbrido)
  tipo_origem?: MedicaoTipoOrigem | null;
  vinculo_id?: string | null;
  os_item_id?: string | null;
  created_at: string;
  updated_at: string;
  // joined from planilha
  item_codigo?: string;
  item_descricao?: string;
  item_unidade?: string;
  item_quantidade_contratada?: number;
}

export interface EmpGlosa {
  id: string;
  boletim_id: string;
  medicao_item_id: string | null;
  tipo: string;
  descricao: string;
  valor_glosa: number;
  fundamentacao: string | null;
  status: 'pendente' | 'aceita' | 'contestada' | 'resolvida';
  contestacao_motivo: string | null;
  contestacao_por: string | null;
  contestacao_em: string | null;
  resolvido_por: string | null;
  resolvido_em: string | null;
  resolucao_observacao: string | null;
  evidencia_ids: string[] | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// ========== HOOKS ==========

export function useEmpBoletins(contratoId: string) {
  return useQuery({
    queryKey: ['emp-boletins', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_boletins_medicao')
        .select('*')
        .eq('contrato_id', contratoId)
        .order('numero_boletim', { ascending: false });
      if (error) throw error;
      return data as EmpBoletimMedicao[];
    },
    enabled: !!contratoId,
  });
}

export function useEmpMedicaoItens(boletimId: string | null) {
  return useQuery({
    queryKey: ['emp-medicao-itens', boletimId],
    queryFn: async () => {
      if (!boletimId) return [];
      const { data, error } = await supabase
        .from('emp_medicao_itens')
        .select('*')
        .eq('boletim_id', boletimId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as EmpMedicaoItem[];
    },
    enabled: !!boletimId,
  });
}

export function useEmpGlosas(boletimId: string | null) {
  return useQuery({
    queryKey: ['emp-glosas', boletimId],
    queryFn: async () => {
      if (!boletimId) return [];
      const { data, error } = await supabase
        .from('emp_glosas')
        .select('*')
        .eq('boletim_id', boletimId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as EmpGlosa[];
    },
    enabled: !!boletimId,
  });
}

// ========== MUTATIONS ==========

export function useEmpMedicoesMutations(contratoId: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['emp-boletins', contratoId] });
    // Auto-invalidate curva S data after any boletim change
    qc.invalidateQueries({ queryKey: ['curva-s-fisica'] });
    qc.invalidateQueries({ queryKey: ['evm-curva-s'] });
    qc.invalidateQueries({ queryKey: ['emp-indicadores'] });
  };

  const createBoletim = useMutation({
    mutationFn: async (input: {
      planilha_versao_id: string;
      numero_boletim: number;
      periodo_inicio: string;
      periodo_fim: string;
      observacoes?: string;
    }) => {
      if (!user) throw new Error('Não autenticado');
      const { data, error } = await supabase.rpc('emp_criar_boletim_com_itens', {
        p_contrato_id: contratoId,
        p_planilha_versao_id: input.planilha_versao_id,
        p_numero_boletim: input.numero_boletim,
        p_periodo_inicio: input.periodo_inicio,
        p_periodo_fim: input.periodo_fim,
        p_observacoes: input.observacoes || null,
        p_created_by: user.id,
      });
      if (error) throw error;
      return data as { boletim_id: string; itens_inseridos: number; valor_bruto: number };
    },
    onSuccess: (result) => {
      invalidate();
      toast({
        title: 'Boletim criado com sucesso',
        description: `${result.itens_inseridos} itens autopopulados · R$ ${result.valor_bruto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      });
    },
    onError: (e: Error) => toast({ title: 'Erro ao criar boletim', description: e.message, variant: 'destructive' }),
  });

  // SLICE F3 — Criação mínima (sentinela). Coexiste com createBoletim legacy.
  // Backend é fonte de verdade: validações reais ficam na RPC + triggers (Safety Layer v2).
  const criarBoletimMinimo = useMutation({
    mutationFn: async (input: {
      periodo_inicio: string;
      periodo_fim: string;
      planilha_versao_id?: string | null;
    }) => {
      if (!user) throw new Error('Não autenticado');
      const { data, error } = await supabase.rpc('emp_criar_boletim_minimo', {
        p_contrato_id: contratoId,
        p_periodo_inicio: input.periodo_inicio,
        p_periodo_fim: input.periodo_fim,
        p_planilha_versao_id: input.planilha_versao_id ?? undefined,
      });
      if (error) throw error;
      const boletimId = data as unknown as string;
      // eslint-disable-next-line no-console
      console.log('[SLICE_F3] criado:', boletimId);
      return boletimId;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Boletim criado (rascunho sentinela)' });
    },
    onError: (e: Error) =>
      toast({ title: 'Erro ao criar boletim', description: e.message, variant: 'destructive' }),
  });

  const updateBoletim = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<EmpBoletimMedicao> & { id: string }) => {
      const { error } = await supabase
        .from('emp_boletins_medicao')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Boletim atualizado' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao atualizar', description: e.message, variant: 'destructive' }),
  });

  const enviarBoletim = useMutation({
    mutationFn: async (boletimId: string) => {
      const { error } = await supabase
        .from('emp_boletins_medicao')
        .update({ status: BOLETIM_STATUS_DB.PENDENTE_APROVACAO_CLIENTE, updated_at: new Date().toISOString() })
        .eq('id', boletimId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Boletim enviado para aprovação' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao enviar', description: e.message, variant: 'destructive' }),
  });

  const enviarParaCliente = useMutation({
    mutationFn: async (boletimId: string) => {
      const { error } = await supabase
        .from('emp_boletins_medicao')
        .update({ status: BOLETIM_STATUS_DB.PENDENTE_APROVACAO_CLIENTE, updated_at: new Date().toISOString() })
        .eq('id', boletimId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Boletim enviado para aprovação do cliente' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao enviar para cliente', description: e.message, variant: 'destructive' }),
  });

  const registrarParecerCliente = useMutation({
    mutationFn: async ({ boletimId, parecer, observacoes }: { boletimId: string; parecer: 'aprovado_integral' | 'reprovado'; observacoes?: string }) => {
      if (!user) throw new Error('Não autenticado');
      const novoStatus = parecer === 'aprovado_integral'
        ? BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO
        : BOLETIM_STATUS_DB.REPROVADO;
      const { error } = await supabase
        .from('emp_boletins_medicao')
        .update({
          status: novoStatus,
          parecer_cliente: parecer,
          parecer_observacoes: observacoes || null,
          parecer_em: new Date().toISOString(),
          parecer_por: user.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', boletimId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Parecer do cliente registrado' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao registrar parecer', description: e.message, variant: 'destructive' }),
  });

  const ajustarItemFaturado = useMutation({
    mutationFn: async ({ medicaoItemId, boletimId, planilhaItemId, novaQuantidade, justificativa }: {
      medicaoItemId: string;
      boletimId: string;
      planilhaItemId: string;
      novaQuantidade: number;
      justificativa: string;
    }) => {
      if (!user) throw new Error('Não autenticado');
      if (!justificativa.trim()) throw new Error('Justificativa obrigatória para ajuste pós-aprovação');

      // 1. Buscar valor anterior
      const { data: itemAtual, error: fetchErr } = await supabase
        .from('emp_medicao_itens')
        .select('quantidade_medida')
        .eq('id', medicaoItemId)
        .single();
      if (fetchErr) throw fetchErr;

      // 2. Registrar no histórico de ajustes
      const { error: histErr } = await supabase
        .from('emp_medicao_ajustes_historico' as any)
        .insert({
          boletim_id: boletimId,
          medicao_item_id: medicaoItemId,
          planilha_item_id: planilhaItemId,
          campo_alterado: 'quantidade_medida',
          valor_anterior: itemAtual.quantidade_medida,
          valor_novo: novaQuantidade,
          justificativa: justificativa.trim(),
          created_by: user.id,
        });
      if (histErr) throw histErr;

      // 3. Atualizar o item (com bypass para permitir edição em faturado)
      // O trigger de bloqueio no boletim não impede edição de itens diretamente
      const { error: updErr } = await supabase
        .from('emp_medicao_itens')
        .update({
          quantidade_medida: novaQuantidade,
          updated_at: new Date().toISOString(),
        })
        .eq('id', medicaoItemId);
      if (updErr) throw updErr;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['emp-medicao-itens', vars.boletimId] });
      qc.invalidateQueries({ queryKey: ['emp-saldo-medicao'] });
      invalidate();
      toast({ title: 'Item ajustado com sucesso. Ajuste registrado na auditoria.' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao ajustar item', description: e.message, variant: 'destructive' }),
  });

  const aprovarBoletim = useMutation({
    mutationFn: async (boletimId: string) => {
      if (!user) throw new Error('Não autenticado');

      // Step 1: Fetch boletim context (contrato_id, numero, valor)
      const { data: boletim, error: fetchErr } = await supabase
        .from('emp_boletins_medicao')
        .select('numero_boletim, valor_liquido, contrato_id')
        .eq('id', boletimId)
        .maybeSingle();
      if (fetchErr) throw fetchErr;
      if (!boletim?.contrato_id) throw new Error('Boletim sem contrato vinculado');

      // Step 2: Hybrid approach — two independent sources for monotony safety
      // Fonte A: último período válido da view com EV > 0 (ignora períodos vazios)
      const { data: indicador } = await supabase
        .from('emp_vw_indicadores_blindados' as any)
        .select('percentual_fisico_realizado, percentual_financeiro_realizado')
        .eq('contrato_id', boletim.contrato_id)
        .gt('ev', 0)
        .order('periodo_referencia', { ascending: false })
        .limit(1)
        .maybeSingle();
      const fonteA_fisico = (indicador as any)?.percentual_fisico_realizado ?? 0;
      const fonteA_finan = (indicador as any)?.percentual_financeiro_realizado ?? 0;

      // Fonte B: MAX histórico dos boletins já aprovados (fonte de verdade do trigger)
      const { data: maxAprovado } = await supabase
        .from('emp_boletins_medicao')
        .select('percentual_fisico_acumulado, percentual_financeiro_acumulado')
        .eq('contrato_id', boletim.contrato_id)
        .eq('status', BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO)
        .order('percentual_fisico_acumulado', { ascending: false })
        .limit(1)
        .maybeSingle();
      const fonteB_fisico = maxAprovado?.percentual_fisico_acumulado ?? 0;
      const fonteB_finan = maxAprovado?.percentual_financeiro_acumulado ?? 0;

      // Resolução: o MAIOR valor garante monotonia perante o trigger
      const pctFisico = Math.max(Number(fonteA_fisico), Number(fonteB_fisico));
      const pctFinan = Math.max(Number(fonteA_finan), Number(fonteB_finan));

      // Step 3: Atomic UPDATE — status + percentages in a single operation
      const { error } = await supabase
        .from('emp_boletins_medicao')
        .update({
          status: BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO,
          aprovado_por: user.id,
          aprovado_em: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          percentual_fisico_acumulado: pctFisico ?? 0,
          percentual_financeiro_acumulado: pctFinan ?? 0,
        })
        .eq('id', boletimId);
      if (error) throw error;

      return {
        numero_boletim: boletim.numero_boletim ?? null,
        valor_liquido: boletim.valor_liquido ?? null,
        percentual_fisico_acumulado: pctFisico,
        percentual_financeiro_acumulado: pctFinan,
      };
    },
    onSuccess: (boletim) => {
      invalidate();
      const vlr = boletim?.valor_liquido != null && Number(boletim.valor_liquido) > 0
        ? fmtCurrency(Number(boletim.valor_liquido))
        : null;
      const pctFisico = boletim?.percentual_fisico_acumulado != null && Number(boletim.percentual_fisico_acumulado) > 0
        ? `${Number(boletim.percentual_fisico_acumulado).toFixed(1)}%`
        : null;
      const pctFinan = boletim?.percentual_financeiro_acumulado != null && Number(boletim.percentual_financeiro_acumulado) > 0
        ? `${Number(boletim.percentual_financeiro_acumulado).toFixed(1)}%`
        : null;
      const parts: string[] = [];
      if (vlr) parts.push(`Valor líquido: ${vlr}`);
      if (pctFisico) parts.push(`Físico acum.: ${pctFisico}`);
      if (pctFinan) parts.push(`Financeiro acum.: ${pctFinan}`);
      toast({
        title: `✅ Boletim ${boletim?.numero_boletim ? `#${boletim.numero_boletim} ` : ''}aprovado para faturamento`,
        description: parts.length > 0 ? parts.join(' · ') : undefined,
      });
    },
    onError: (e: Error) => toast({ title: 'Erro ao aprovar', description: e.message, variant: 'destructive' }),
  });

  const rejeitarBoletim = useMutation({
    mutationFn: async ({ boletimId, motivo }: { boletimId: string; motivo: string }) => {
      const { error } = await supabase
        .from('emp_boletins_medicao')
        .update({
          status: BOLETIM_STATUS_DB.REPROVADO,
          observacoes: motivo,
          updated_at: new Date().toISOString(),
        })
        .eq('id', boletimId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Boletim rejeitado' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao rejeitar', description: e.message, variant: 'destructive' }),
  });

  const reabrirBoletim = useMutation({
    mutationFn: async ({ boletimId, motivo }: { boletimId: string; motivo: string }) => {
      if (!user) throw new Error('Não autenticado');
      const { error } = await supabase
        .from('emp_boletins_medicao')
        .update({
          status: BOLETIM_STATUS_DB.RASCUNHO,
          motivo_reabertura: motivo,
          reaberto_por: user.id,
          reaberto_em: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', boletimId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Boletim reaberto' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao reabrir', description: e.message, variant: 'destructive' }),
  });

  // --- Itens de Medição ---
  const upsertMedicaoItem = useMutation({
    mutationFn: async (item: {
      boletim_id: string;
      planilha_item_id: string;
      quantidade_medida: number;
      quantidade_acumulada_anterior: number;
      valor_unitario: number;
      observacoes?: string;
    }) => {
      const quantidade_acumulada_atual = item.quantidade_acumulada_anterior + item.quantidade_medida;
      const valor_periodo = item.quantidade_medida * item.valor_unitario;
      const valor_acumulado = quantidade_acumulada_atual * item.valor_unitario;

      const { data, error } = await supabase
        .from('emp_medicao_itens')
        .upsert({
          boletim_id: item.boletim_id,
          planilha_item_id: item.planilha_item_id,
          quantidade_medida: item.quantidade_medida,
          quantidade_acumulada_anterior: item.quantidade_acumulada_anterior,
          quantidade_acumulada_atual,
          valor_unitario: item.valor_unitario,
          valor_periodo,
          valor_acumulado,
          observacoes: item.observacoes || null,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'boletim_id,planilha_item_id', ignoreDuplicates: false })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['emp-medicao-itens', vars.boletim_id] });
    },
    onError: (e: Error) => toast({ title: 'Erro ao salvar item', description: e.message, variant: 'destructive' }),
  });

  // --- Glosas ---
  const createGlosa = useMutation({
    mutationFn: async (input: {
      boletim_id: string;
      medicao_item_id?: string | null;
      tipo: string;
      descricao: string;
      valor_glosa: number;
      fundamentacao?: string;
    }) => {
      if (!user) throw new Error('Não autenticado');
      const { data, error } = await supabase
        .from('emp_glosas')
        .insert({
          boletim_id: input.boletim_id,
          medicao_item_id: input.medicao_item_id || null,
          tipo: input.tipo,
          descricao: input.descricao,
          valor_glosa: input.valor_glosa,
          fundamentacao: input.fundamentacao || null,
          status: 'pendente' as const,
          created_by: user.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['emp-glosas', vars.boletim_id] });
      toast({ title: 'Glosa registrada' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao criar glosa', description: e.message, variant: 'destructive' }),
  });

  const resolverGlosa = useMutation({
    mutationFn: async ({ id, boletimId, observacao }: { id: string; boletimId: string; observacao: string }) => {
      if (!user) throw new Error('Não autenticado');
      const { error } = await supabase
        .from('emp_glosas')
        .update({
          status: 'resolvida' as const,
          resolvido_por: user.id,
          resolvido_em: new Date().toISOString(),
          resolucao_observacao: observacao,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['emp-glosas', vars.boletimId] });
      toast({ title: 'Glosa resolvida' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao resolver glosa', description: e.message, variant: 'destructive' }),
  });

  return {
    createBoletim,
    criarBoletimMinimo,
    updateBoletim,
    enviarBoletim,
    enviarParaCliente,
    registrarParecerCliente,
    ajustarItemFaturado,
    aprovarBoletim,
    rejeitarBoletim,
    reabrirBoletim,
    upsertMedicaoItem,
    createGlosa,
    resolverGlosa,
  };
}

// ========== HELPERS ==========

export const BOLETIM_STATUS_LABELS: Record<BoletimStatusDB, string> = {
  [BOLETIM_STATUS_DB.RASCUNHO]: 'Rascunho',
  [BOLETIM_STATUS_DB.PENDENTE_APROVACAO_CLIENTE]: 'Pendente Aprovação Cliente',
  [BOLETIM_STATUS_DB.REPROVADO]: 'Reprovado',
  [BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO]: 'Aprovado p/ Faturamento',
};

export const BOLETIM_STATUS_COLORS: Record<BoletimStatusDB, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning'> = {
  [BOLETIM_STATUS_DB.RASCUNHO]: 'outline',
  [BOLETIM_STATUS_DB.PENDENTE_APROVACAO_CLIENTE]: 'warning',
  [BOLETIM_STATUS_DB.REPROVADO]: 'destructive',
  [BOLETIM_STATUS_DB.APROVADO_PARA_FATURAMENTO]: 'success',
};

export const GLOSA_TIPO_OPTIONS = [
  { value: 'qualidade', label: 'Qualidade' },
  { value: 'quantidade', label: 'Quantidade' },
  { value: 'documentacao', label: 'Documentação' },
  { value: 'prazo', label: 'Prazo' },
  { value: 'seguranca', label: 'Segurança' },
  { value: 'outro', label: 'Outro' },
];
